import React, { useEffect, useContext, useState } from "react"

import _ from "lodash"

import { updateAirportSeasonsSelection } from "../containers/selectionSlice"

import { AirportSeasonConfigurations } from "../api/AirportSeasonConfiguration"
import { ValueLabel } from "./generic/fields"
import DropdownField from "./generic/form/DropdownField"
import RadioBooleanBtnsField from "./generic/form/RadioBooleanBtnsField"
import { EditContext, ContextType } from "../pages/editFlights/Module"

import RadioControls from "./generic/form/RadioControls"

import CustomModal from "./modal"
import { warningSelectSeason, warningSelectTime } from "../common/pageText"
import { CommonText, FieldName, FlightText } from "../common/text"
import * as Utils from "./Utils"
import { AddContext } from "../pages/addFlights/Module"
import Portal from "../common/Portal"
import { useAppDispatch, useAppSelector } from "../app/hooks"

interface Props {
  airportSeasons: AirportSeasonConfigurations
  supportsICAO: boolean
  seasonReadonly?: boolean
  timeReadonly?: boolean
  emptyMatrix?: () => void
}
const { time, utcText, localText } = FlightText
const SeasonSelector: React.FunctionComponent<Props> = ({
  airportSeasons,
  supportsICAO,
  seasonReadonly,
  timeReadonly,
  emptyMatrix
}) => {
  const dispatch = useAppDispatch()

  const { season, countries, airports, useLocalTime } = useAppSelector(
    (state) => state["selectionReducer"].airportSeasonsSelector
  )
  const seasonsData = useAppSelector((state) => state["appReducer"].seasons)

  //init season if not saved in store
  useEffect(() => {
    if (!season) {
      const timeAirportSeasons =
        useLocalTime !== undefined
          ? airportSeasons.filter(
              (as) =>
                (useLocalTime ? as.supportsLt : as.supportsUtc) &&
                (supportsICAO ? as.supportsIcao : as.supportsIata)
            )
          : airportSeasons
      const uniqSeasons = _.uniqBy(timeAirportSeasons, (a) => a.season)

      const today = Utils.formatDate(new Date())
      const currentSeason = seasonsData.find(
        (sd) => today < sd.endDate && today > sd.startDate
      )

      const preSelectedSeason = currentSeason
        ? uniqSeasons.find((s) => currentSeason.code === s.season)
        : null

      if (uniqSeasons.length > 0) {
        dispatch(
          updateAirportSeasonsSelection({
            field: FieldName.season,
            value: preSelectedSeason
              ? preSelectedSeason.season
              : uniqSeasons[0].season
          })
        )
      }
    }
  }, [
    airportSeasons,
    useLocalTime,
    season,
    supportsICAO,
    dispatch,
    seasonsData
  ])

  const handleGetSeasonOpts = () => {
    const as = airportSeasons.filter((as) =>
      supportsICAO ? as.supportsIcao : as.supportsIata
    )
    const uniqSeasons = _.uniqBy(as, (a) => a.season)
    uniqSeasons.sort((a, b) => {
      const aData = seasonsData.find((sd) => sd.code === a.season)
      const bData = seasonsData.find((sd) => sd.code === b.season)
      return aData && bData && aData.startDate < bData.startDate ? 1 : -1
    })
    const options: Array<ValueLabel> = uniqSeasons.map((as) => ({
      label: as.season.toString(),
      value: as.season.toString()
    }))
    return options
  }
  const seasonsOpts = handleGetSeasonOpts()

  useEffect(() => {
    if (seasonsOpts.length && season) {
      const chosenSeasonExists = seasonsOpts.find((so) => so.value === season)
      if (!chosenSeasonExists) {
        dispatch(
          updateAirportSeasonsSelection({
            field: FieldName.season,
            value: seasonsOpts[0].value
          })
        )
      }
    }
  }, [seasonsOpts, season, dispatch])

  const [showSeasonModal, setShowSeasonModal] = useState(false)
  const [seasonValue, setSeasonValue] = useState("")

  const editContext = useContext<ContextType>(EditContext)

  const addContext = useContext<ContextType>(AddContext)

  const context = addContext || editContext

  const closeModal = () => {
    setShowSeasonModal(false)
  }
  const disp = (val: string) => {
    setShowSeasonModal(false)
    context.handleDispatchSeasons && context.handleDispatchSeasons()
    const updSelectedAirports = airports.length
      ? airports.filter((sa) =>
          airportSeasons.find((a) => a.airport === sa && a.season === val)
        )
      : []
    const updSelectedCountries = countries.length
      ? countries.filter((sc) =>
          airportSeasons.find((as) => as.country === sc && as.season === val)
        )
      : []
    dispatch(
      updateAirportSeasonsSelection({ field: FieldName.season, value: val })
    )

    dispatch(
      updateAirportSeasonsSelection({
        field: FieldName.airports,
        value: updSelectedAirports.map((v) => {
          return v
        })
      })
    )
    dispatch(
      updateAirportSeasonsSelection({
        field: FieldName.countries,
        value: updSelectedCountries.map((v) => {
          return v
        })
      })
    )
  }
  const handleSelectSeason = (name: string, val: string) => {
    setSeasonValue(val)

    if (context && context.activeSearch && val.length > 1) {
      setShowSeasonModal(true)
    } else {
      disp(val)
      setShowSeasonModal(false)
    }
  }

  const handleCheckSeasonSupportsTime = (localTime: boolean): boolean => {
    const supportsTime =
      airportSeasons.filter(
        (as) =>
          as.season === season && (localTime ? as.supportsLt : as.supportsUtc)
      ).length > 0

    return supportsTime
  }

  useEffect(() => {
    if (
      airportSeasons.length > 0 &&
      airportSeasons.find((as) => as.season === season) &&
      !handleCheckSeasonSupportsTime(useLocalTime)
    ) {
      dispatch(
        updateAirportSeasonsSelection({
          field: FieldName.useLocalTime,
          value: !useLocalTime
        })
      )
    }
    // eslint-disable-next-line
  }, [season, airportSeasons])

  return (
    <div className="d-flex season-time-container">
      <div className="ocs-mr-12 ocs-w-72">
        <DropdownField
          name={FieldName.season}
          caption="Season"
          clearable={false}
          closeOnSelect
          options={seasonsOpts}
          value={season}
          readonly={seasonReadonly}
          onChange={handleSelectSeason}
          autoFocus
          menuPortalTarget={document.body}
        />
      </div>
      {!context?.showContext ? (
        <RadioBooleanBtnsField
          caption={time}
          value={useLocalTime}
          onChange={(name: string, val: boolean) => {
            emptyMatrix && emptyMatrix()
            dispatch(
              updateAirportSeasonsSelection({
                field: FieldName.useLocalTime,
                value: val
              })
            )
          }}
          options={[
            { label: utcText, value: false },
            { label: localText, value: true }
          ]}
          readonly={timeReadonly}
        />
      ) : (
        <div className="ocs-time-container ocs-field-container">
          <label className="ocs-mb-4"> {time}</label>
          <div className="d-flex mq-flex-direction-row">
            {!timeReadonly ? (
              context.timeOptions.map((timeOption) => {
                return (
                  <RadioControls
                    key={timeOption.id}
                    value={timeOption.value}
                    label={timeOption.label}
                    inputIdentifier={timeOption.id}
                    name="time"
                    onChange={context.handleTimeChange}
                    checked={context.selectedTime === timeOption.value}
                    className={"btn-radio"}
                    disabled={
                      airportSeasons.length > 0 &&
                      airportSeasons.find((as) => as.season === season)
                        ? !handleCheckSeasonSupportsTime(
                            timeOption.id === "local" ? true : false
                          )
                        : false
                    }
                  />
                )
              })
            ) : (
              <p className="ocs-field-readonly">
                {context.selectedTime === "local" ? localText : utcText}
              </p>
            )}
          </div>
        </div>
      )}

      {showSeasonModal ? (
        <Portal>
          <CustomModal
            isOpen={showSeasonModal}
            title={warningSelectSeason.title}
            ctaText={CommonText.ok}
            showCancel
            onCallback={() => disp(seasonValue)}
            onToggle={closeModal}
            position="top"
            size={"sm"}
          >
            {warningSelectSeason.content}
          </CustomModal>
        </Portal>
      ) : null}
      {context.showModal && (
        <Portal>
          <CustomModal
            isOpen={context.showModal}
            title={warningSelectTime.title}
            ctaText={CommonText.ok}
            showCancel
            onCallback={context.handleDispatchTime}
            onToggle={context.handleCloseModal}
            position="top"
            size={"sm"}
          >
            {warningSelectTime.content}
          </CustomModal>
        </Portal>
      )}
    </div>
  )
}

SeasonSelector.defaultProps = {
  seasonReadonly: false,
  timeReadonly: false
}

export default SeasonSelector
